<template>
  <b-container fluid>
    <TicketNavBar @new-ticket="handleNewTicketModal" />
    <br><br>

    <!-- Search bar and filter -->
    <b-row class="mt-3">
      <b-col cols="12" md="6" lg="4">
        <div class="d-flex align-items-center">
          <b-form-group style="min-width: 88%" class="mb-0">
            <b-input-group>
              <b-input
                v-model="searchTerm"
                autofocus
                placeholder="Buscar por id, título o descripción"
                @keyup.enter="search"
              />
              <b-input-group-append class="mb-0">
                <b-button variant="outline-secondary" @click="search">
                  <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-form-group class="ml-3 mb-0">
            <b-button variant="outline-secondary" @click="resetSearch">
              <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width />
            </b-button>
          </b-form-group>
        </div>
      </b-col>
      <b-col cols="12" md="6" lg="8">
        <div class="d-flex justify-content-between align-items-baseline flex-wrap flex-sm-nowrap">
          <div class="d-md-none d-flex align-items-baseline w-100 mt-2 mt-sm-0">
            <b-checkbox
              v-model="openStatusFilter"
              value="open"
              :disabled="searching"
              switch
              style="min-width: 120px;"
              @change="onOpenStatusFilterChange('open', $event)"
            >
              Abiertos <sup v-show="!searching && openStatusFilter === 'open'">{{ count }}</sup>
            </b-checkbox>
            <b-checkbox
              v-model="openStatusFilter"
              value="closed"
              :disabled="searching"
              switch
              class="ml-2"
              style="min-width: 120px;"
              @change="onOpenStatusFilterChange('closed', $event)"
            >
              Cerrados <sup v-show="!searching && openStatusFilter === 'closed'">{{ count }}</sup>
            </b-checkbox>
          </div>
          <div class="d-flex align-items-center justify-content-end mt-2 mt-md-0 w-100">
            <b-dropdown
              variant="link"
              toggle-class="custom-toggle"
              no-flip
              class="ml-2 d-none d-lg-inline-block px-1"
            >
              <template #button-content>
                Editar columnas
              </template>

              <b-dropdown-form style="min-width: 230px;">
                <b-form-checkbox-group v-model="selectedColumns" stacked>
                  <b-form-checkbox
                    v-for="col in allColumns"
                    :key="col.key"
                    :value="col.key"
                    :disabled="col.locked"
                  >
                    {{ col.label }}
                  </b-form-checkbox>
                </b-form-checkbox-group>
              </b-dropdown-form>
              <b-dropdown-divider />
              <b-dropdown-item-button @click="resetColumns" class="text-center">
                <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width  size="sm"/>
                <span class="fs-15">Restablecer</span>
              </b-dropdown-item-button>
            </b-dropdown>

            <b-form-group id="input-group-1" class="mb-0 ml-0 ml-sm-3">
              <b-form-select
                id="input-1"
                v-model="statusFilter"
                :options="statusFilterOptions"
                style="min-width: 300px;"
                :disabled="searching"
                @change="search"
              >
                <template #first>
                  <b-select-option :value="null">
                    <span v-if="!statusFilter" class="text-danger">-- Filtrar por estado --</span>
                    <span v-else >-- Quitar filtro ---</span>
                  </b-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Items count and filter -->
    <b-row class="mt-2 mb-0" align-v="center">
      <b-col>
        <b-button-group>
          <b-button
            :variant="viewFilter === 'requested_tickets' ? 'primary' : 'outline-light'"
            :class="{ 'text-dark': viewFilter !== 'requested_tickets' }"
            @click="onViewFilterChange('requested_tickets')"
          >
            Solicitados
          </b-button>
          <b-button
            :variant="viewFilter === 'followed_tickets' ? 'primary' : 'outline-light'"
            :class="{ 'text-dark': viewFilter !== 'followed_tickets' }"
            @click="onViewFilterChange('followed_tickets')"
          >
            Seguidos
          </b-button>
          <b-button
            :variant="viewFilter === 'assigned_tickets' ? 'primary' : 'outline-light'"
            :class="{ 'text-dark': viewFilter !== 'assigned_tickets' }"
            @click="onViewFilterChange('assigned_tickets')"
          >
            Asignados
          </b-button>
        </b-button-group>
      </b-col>
      <b-col>
        <div class="d-none d-md-block d-md-flex justify-content-end align-items-baseline">
          <b-checkbox
            v-model="openStatusFilter"
            value="open"
            :disabled="searching"
            switch
            size="lg"
            style="min-width: 140px;"
            @change="onOpenStatusFilterChange('open', $event)"
          >
            Abiertos <sup v-show="!searching && openStatusFilter === 'open'">{{ count }}</sup>
          </b-checkbox>
          <b-checkbox
            v-model="openStatusFilter"
            value="closed"
            class="ml-2"
            :disabled="searching"
            switch
            size="lg"
            style="min-width: 140px;"
            @change="onOpenStatusFilterChange('closed', $event)"
          >
            Cerrados <sup v-show="!searching && openStatusFilter === 'closed'">{{ count }}</sup>
          </b-checkbox>
        </div>
      </b-col>
    </b-row>

    <!-- Table -->
    <b-row class="mt-2 d-none d-xl-block">
      <b-col>
        <b-table
          :items="items"
          :fields="fields"
          class="fs-15"
          show-empty
          caption-top
          hover
          responsive
          no-local-sorting
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          @sort-changed="sortColumn"
          @row-clicked="onRowClick"
        >
          <template #empty>
            <div v-if="loading" class="text-center text-secondary">
              <b-spinner small type="grow"></b-spinner>
              <span class="pl-2 fs-18">Cargando tickets</span>
            </div>
            <div v-else class="text-center font-italic">
              <span v-if="searchTerm" class="text-danger fs-18">No hay resultados</span>
              <span v-else class="text-secondary fs-18">No hay tickets todavía</span>
            </div>
          </template>

          <template #cell(title)="data">
            <span class="line-clamp-3" :title="data.value">{{ data.value }}</span>
          </template>

          <template #cell(request_type)="data">
            <div style="max-width: 450px; overflow-wrap: anywhere; word-break: break-word;">
              {{ data.value }}
            </div>
          </template>


          <template #cell(description)="data">
            <span class="line-clamp-3" :title="data.value">{{ data.value }}</span>
          </template>

          <template #cell(id)="data">
            <span class="text-primary font-weight-bold">{{ data.value }}</span>
          </template>

          <template #cell(status)="data">
            <b-badge class="fs-12" pill :variant="badgeStatusMap[data.value].variant || 'info'" style="font-size: 90%">
              {{badgeStatusMap[data.value].text || data.value}}
            </b-badge>
          </template>

          <template #cell(user_id)="data">
            <NameInitialsIcon :user="data.item.user" :color="data.item.color" />
          </template>

          <template #cell(agent_id)="data">
            <span>{{ data.item.agent | concatName2 | titleize }}</span>
          </template>

          <template #cell(updated_at)="data">
            <span>{{ data.value | formatTimeAgo }}</span>
          </template>

          <template #cell(created_at)="data">
            <span>{{ data.value | formatDate }}</span>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-row class="mt-2 d-xl-none">
      <b-col
        v-for="item in items"
        :key="item.id"
        cols="12"
        class="mb-3"
      >
        <b-card
          class="shadow-sm"
          @click="onRowClick(item)"
          style="cursor: pointer; border-radius: 10px;"
        >
          <div class="header">
            <div class="d-flex justify-content-between align-items-center">
              <NameInitialsIcon :user="item.user" :color="item.color" />
              <div class="text-muted small text-right d-flex flex-column justify-content-center align-items-end">
                <div class="d-flex justify-content-end align-items-center">
                  <div class="mr-2"><font-awesome-icon icon="calendar-alt" /> {{ item.created_at | formatDate }}</div>
                  <b-badge :variant="badgeStatusMap[item.status].variant" pill>
                    <span>{{ badgeStatusMap[item.status].text }}</span>
                  </b-badge>
                </div>
                <div>Actualizado {{ item.updated_at | formatTimeAgo }}</div>
              </div>
            </div>
          </div>
          <div>
            <p class="mb-0 mt-2 fs-17 line-clamp-3" :title="item.title">{{ item.title }}</p>
          </div>
        </b-card>
      </b-col>

      <!-- Empty / Loading States -->
      <b-col v-if="!items.length && !loading">
        <p class="text-center text-muted font-italic">
          <span v-if="searchTerm" class="text-danger fs-18">No hay resultados</span>
          <span v-else class="fs-18">No hay tickets todavía</span>
        </p>
      </b-col>
      <b-col v-if="loading">
        <div class="text-center text-secondary">
          <b-spinner small type="grow"></b-spinner>
          <span class="pl-2 fs-18">Cargando tickets</span>
        </div>
      </b-col>
    </b-row>

    <!-- Pagination -->
    <b-row>
      <b-col>
        <div class="d-flex justify-content-center my-3">
          <PaginationCountless :page="page" :next="next" :prev="prev">
            <template #prev-label>
              <font-awesome-icon :icon="['fas', 'backward']" size="lg" fixed-width />
            </template>
            <template #next-label>
              <font-awesome-icon :icon="['fas', 'forward']" size="lg" fixed-width />
            </template>
          </PaginationCountless>
        </div>
      </b-col>
    </b-row>

    <!-- Modals -->
    <NewTicketModal
      v-if="showNewTicketModal"
      @close="showNewTicketModal = false"
      @on-create-ticket="handleNewTicket"
    />
  </b-container>
</template>

<script>
import { normalize, schema } from 'normalizr'
import { badgeStatusMap, statusOptions, closedStatuses, ticketColumns } from '../../../sharedOptions/ticketsModule'

import TicketNavBar from './NavBar'
import NameInitialsIcon from '../../shared/NameInitialsIcon.vue'
import PaginationCountless from '../../shared/PaginationCountless'
import NewTicketModal from './NewTicketModal'

export default {
  name: 'Index',
  components: { TicketNavBar, PaginationCountless, NewTicketModal, NameInitialsIcon },
  data () {
    return {
      loading: false,
      searchTerm: '',
      searching: false,
      page: 1,
      next: null,
      prev: null,
      count: 0,
      showNewTicketModal: false,
      statusFilters: statusOptions,
      badgeStatusMap: badgeStatusMap,
      ticketData: {},
      ticketList: []
    }
  },
  mounted () {
    this.fetchTickets()
  },
  watch: {
    '$route.query' (to, from) {
      this.fetchTickets()
    }
  },
  computed: {
    items () {
      if (this.loading) return []

      const ticketList = structuredClone(this.ticketList)

      return ticketList.map(id => {
        const ticket = this.ticketData[id]

        this.$store.commit('setUserColor', ticket.user_id)
        ticket.color = this.$store.getters['userColorById'](ticket.user_id)
        ticket.request_type = ticket?.internal_ticket_level?.request_type

        return ticket
      })
    },
    currentUserId () {
      return this.$store.state.currentUser?.id
    },
    allColumns () {
      const fields = [...ticketColumns]
      return fields.sort((a, b) => a.locked ? -1 : 1)
    },
    fields () {
      return this.$store.getters['internalTickets/visibleFields']
    },
    selectedColumns: {
      get () {
        return this.fields.map(f => f.key)
      },
      set (keys) {
        this.$store.commit('internalTickets/setVisibleColumnKeys', keys)
      }
    },
    viewFilter: {
      get() {
        return this.$store.state.internalTickets.ticketViewFilter
      },
      set(val) {
        this.$store.commit('internalTickets/setTicketViewFilter', val)
      }
    },
    openStatusFilter: {
      get() {
        return this.$store.state.internalTickets.openStatusFilter
      },
      set(val) {
        this.$store.commit('internalTickets/setOpenStatusFilter', val)
      }
    },
    statusFilter: {
      get() {
        return this.$store.state.internalTickets.statusFilter
      },
      set(val) {
        this.$store.commit('internalTickets/setStatusFilter', val)
      }
    },
    sortBy: {
      get() {
        return this.$store.state.internalTickets.sortBy
      },
      set(val) {
        this.$store.commit('internalTickets/setSortBy', val)
      }
    },
    sortDesc: {
      get() {
        return this.$store.state.internalTickets.sortDesc
      },
      set(val) {
        this.$store.commit('internalTickets/setSortDesc', val)
      }
    },
    statusFilterOptions () {
      const options = this.statusFilters
      if (this.openStatusFilter === 'open') {
        return options.filter(s => s.value  !== 'resuelto' && s.value !== 'desestimado' && s.value !== 'pasa_a_proyecto')
      }

      return options.filter(s => s.value  === 'resuelto' || s.value === 'desestimado' || s.value === 'pasa_a_proyecto')
    }
  },
  methods: {
    fetchTickets () {
      if (this.loading) { return }
      this.loading = true
      this.$http.get('/api/production/internal_tickets', {
        params: {
          page: this.$route.query.pagina,
          status_filter: this.statusFilter,
          q: this.searchTerm,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
          view_filter: this.viewFilter,
          open_status_filter: this.openStatusFilter === 'open' ? 'true' : 'false'
        }
      })
        .then(response => {
          // console.log(response)
          const { tickets, pagy } = response.data
          this.formatData(tickets)
          this.addPagination(pagy)
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.loading = false
          this.searching = false
        })
    },
    search () {
      if (this.searching) { return }
      this.searching = true
      let query = Object.assign({}, this.$route.query)
      if (query.pagina) {
        delete query.pagina
        this.$router.replace({ query })
          .catch( (error)=>{
            if(error.name !== 'NavigationDuplicated' && !error.message.includes('Avoided redundant navigation to current location')){
              console.log(error)
            }
        })
      } else {
        this.fetchTickets()
      }
    },
    formatData (tickets) {
      const ticketData = normalize(
        { items: tickets },
        { items: [ new schema.Entity('items') ] }
      )

      if (ticketData.entities.hasOwnProperty('items')) {
        this.ticketData = ticketData.entities.items
      } else {
        this.ticketData = {}
      }
      this.ticketList = ticketData.result.items
    },
    addPagination(pagy) {
      this.page = pagy.page
      this.count = pagy.count
      if (pagy.page < pagy.last) {
        this.next = pagy.page + 1
      } else {
        this.next = null
      }
      if (pagy.page != 1 && pagy.page > 1) {
        this.prev = pagy.page - 1
      } else {
        this.prev = null
      }
    },
    resetSearch () {
      this.searchTerm = ''
      this.search()
    },
    resetColumns () {
      this.$store.commit('internalTickets/setVisibleColumnKeys', null)
    },
    handleNewTicketModal () {
      this.showNewTicketModal = true
    },
    onRowClick (item) {
      if (item.id) { this.$router.push(`/app/tickets/${item.id}`) }
    },
    handleNewTicket (ticket) {
      this.ticketData[ticket.id] = ticket
      this.ticketList.unshift(ticket.id)
      this.$router.push(`/app/tickets/${ticket.id}`)
    },
    sortColumn(ctx) {
      if (ctx.sortBy) {
        this.sortBy = ctx.sortBy
        this.sortDesc = ctx.sortDesc
        this.fetchTickets()
      }
    },
    onViewFilterChange (filter) {
      if (filter === this.viewFilter) return

      this.viewFilter = filter
      this.search()
    },
    onOpenStatusFilterChange (switchName, value) {
      if (switchName === 'open') {
        this.openStatusFilter = value === 'open' ? value : 'closed'
      } else {
        this.openStatusFilter = value === 'closed' ? value : 'open'
      }

      this.statusFilter = null
      this.search()
    }
  }
}
</script>

<style scoped>
  ::v-deep .cell-text {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .line-clamp-2,
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .line-clamp-2 { -webkit-line-clamp: 2; }
  .line-clamp-3 { -webkit-line-clamp: 3; }

  .columns-dropdown ::v-deep .dropdown-toggle {
    min-width: 200px;
    font-weight: 400;
  }

  .columns-dropdown ::v-deep .dropdown-toggle:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    text-decoration: none;
  }

::v-deep .custom-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  min-width: 200px;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  text-decoration: none;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

::v-deep .custom-toggle:hover,
::v-deep .custom-toggle:active {
  color: #495057;
  text-decoration: none;
}

::v-deep .custom-toggle:focus,
::v-deep .show > .custom-toggle {
  color: #495057;
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  text-decoration: none;
}

::v-deep .custom-toggle::after {
  margin-left: 0.75rem;
  color: #6c757d;
}
</style>
